/**
 * Collapsible (13.2-11, D-13.2-A-5): the record page's disclosure, so a long record collapses to its headers. The
 * header is a `<button aria-expanded aria-controls>` inside the heading (the WAI-ARIA disclosure / accordion pattern),
 * holding the existing panel or section title and a chevron; Enter and Space toggle it, as any button.
 *
 * The body stays mounted while closed (`hidden`), so a half-typed form survives a collapse; `hidden` keeps it out of
 * the tab order and the accessibility tree. When closed, a quiet hint says what is inside ("12 fields", "3 entries").
 * A `tag` (e.g. "Read only") shows open or closed. Motion is CSS only (the chevron turns, the body fades in), and is
 * off under `prefers-reduced-motion`. No colours of its own: the card is the caller's (`cardStyles`).
 */

import { useId } from 'react';
import type { ReactNode } from 'react';
import styles from './record.module.css';

export interface CollapsibleProps {
  /** The heading text: a panel or section title. */
  title: ReactNode;
  open: boolean;
  onToggle: () => void;
  /** The heading level: 3 for panels and sections (under the page's h2 areas), 2 for an area. */
  level?: 2 | 3;
  /** The outer element's id (`panel-<key>`), so a `#panel-<key>` link lands on it. */
  id?: string;
  /** The outer element's class: the card (`cardStyles.panel`, `cardStyles.section`), or none for an area. */
  className?: string;
  /** The heading's own class (`cardStyles.panelTitle`, `cardStyles.sectionTitle`, the page's area title). */
  titleClassName?: string;
  /** Shown beside the title, open or closed ("Read only"). */
  tag?: ReactNode;
  /** Shown only while closed: what is inside ("12 fields", "3 to add"). */
  hint?: string | null;
  /** `aria-disabled` on the card (the "arrives in the next update" slot). */
  disabled?: boolean;
  children: ReactNode;
}

export default function Collapsible({
  title,
  open,
  onToggle,
  level = 3,
  id,
  className,
  titleClassName,
  tag,
  hint,
  disabled,
  children,
}: CollapsibleProps) {
  const bodyId = `${useId()}-body`;
  const Heading = level === 2 ? 'h2' : 'h3';
  return (
    <div className={[className, styles.collapsible].filter(Boolean).join(' ')} id={id} data-open={open ? 'true' : undefined} aria-disabled={disabled || undefined}>
      <Heading className={[titleClassName, styles.disclosureHeading].filter(Boolean).join(' ')}>
        <button type="button" className={styles.disclosure} aria-expanded={open} aria-controls={bodyId} onClick={onToggle}>
          <span className={styles.disclosureTitle}>{title}</span>
          {tag}
          {!open && hint && <span className={styles.disclosureHint}>{hint}</span>}
          <svg className={styles.disclosureChev} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>
      </Heading>
      <div id={bodyId} className={styles.disclosureBody} hidden={!open}>
        {children}
      </div>
    </div>
  );
}
